/**
 * Chapter 4 Cue Choice Component - Sparkling Petal (Kindness)
 *
 * Trauma-informed kindness activity offering two approaches:
 * Option 1: "Kindness Spark!" — chant, tap heart, say one kind word aloud (original exercise)
 * Option 2: "Quiet Acts" — behavioral approach where the child quietly thinks of one
 *            small act of kindness they could DO, removing the pressure to perform
 *            kindness verbally or publicly
 *
 * Based on the same dual-path architecture as BreathingCueChoice (Chapter 1).
 */

import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@/components/ui/button";
import { X, Sparkles, Hand, Info } from "lucide-react";

interface Chapter4CueChoiceProps {
  onComplete: () => void;
  isCompleted: boolean;
  trackButton?: (buttonId: string, metadata?: any) => void;
}

/** Shows one of the two activity modes step-by-step */
function ActivityMode({ mode, onBack, onComplete }: { mode: 'spark' | 'quiet'; onBack: () => void; onComplete: () => void }) {
  const [currentStep, setCurrentStep] = useState(0);

  const sparkSteps = [
    "Get ready to spread some kindness!",
    "Chant together: \"Kindness spark!\"",
    "Tap your heart gently with your hand.",
    "Turn to someone nearby (or a character in the book).",
    "Say one kind word out loud — anything counts!"
  ];

  const quietSteps = [
    "Let's think about a quiet act of kindness.",
    "Look around — is there someone nearby who might need something?",
    "Think of ONE small thing you could do — hold a door, share a snack, smile.",
    "You don't have to say it out loud — just picture yourself doing it.",
    "Kindness doesn't need an audience. Your heart already knows."
  ];

  const steps = mode === 'spark' ? sparkSteps : quietSteps;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center">
        <div className="inline-flex items-center gap-2 text-lg font-semibold text-pink-800">
          {mode === 'spark' ? (
            <><Sparkles className="w-5 h-5 text-pink-500" /> Kindness Spark!</>
          ) : (
            <><Hand className="w-5 h-5 text-rose-400" /> Quiet Acts</>
          )}
        </div>
      </div>

      <div className="bg-pink-50 rounded-lg p-6 min-h-[120px] flex items-center justify-center border-2 border-pink-200">
        <AnimatePresence mode="wait">
          <motion.p
            key={currentStep}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="text-xl text-gray-800 text-center leading-relaxed"
          >
            {steps[currentStep]}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Animated visual */}
      <div className="flex justify-center">
        <motion.div
          className={`w-20 h-20 rounded-full ${
            mode === 'spark'
              ? 'bg-gradient-to-br from-pink-400 to-rose-400'
              : 'bg-gradient-to-br from-rose-300 to-pink-200'
          }`}
          animate={mode === 'spark'
            ? { scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }
            : { opacity: [0.7, 1, 0.7], scale: [1, 1.05, 1] }
          }
          transition={{ duration: mode === 'spark' ? 1.5 : 3, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-2">
        {steps.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentStep(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentStep ? 'bg-pink-600 w-6' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>

      <div className="flex justify-between items-center pt-2">
        <Button variant="ghost" onClick={onBack} className="text-gray-600 hover:text-gray-800">
          ← Back to choices
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCurrentStep(p => Math.max(0, p - 1))} disabled={currentStep === 0} className="text-sm">Previous</Button>
          <Button variant="outline" onClick={() => setCurrentStep(p => Math.min(steps.length - 1, p + 1))} disabled={currentStep === steps.length - 1} className="text-sm">Next</Button>
        </div>
      </div>

      {currentStep === steps.length - 1 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center pt-2">
          <Button onClick={onComplete} className="bg-pink-600 hover:bg-pink-700 text-white px-8 py-3 text-base font-medium">
            {mode === 'spark' ? 'I Kindness Sparked!' : 'I thought of a kind act!'}
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
}

/** Parent/Educator info panel */
function EducatorInfo({ onClose }: { onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 space-y-3"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-600" />
          <h4 className="font-semibold text-blue-900">For Parents & Educators</h4>
        </div>
        <button onClick={onClose} className="text-blue-400 hover:text-blue-600"><X className="w-4 h-4" /></button>
      </div>
      <div className="text-sm text-blue-800 space-y-2">
        <p><strong>Why two approaches to kindness?</strong></p>
        <p>For some children, performing kindness publicly—saying kind words aloud in front of others—feels exposing or performative. Children who have experienced difficult social dynamics may feel unsafe expressing warmth openly.</p>
        <p><strong>"Quiet Acts"</strong> honors kindness as an internal intention, not just an observable behavior. Research on prosocial behavior shows that imagining a kind act activates the same neural pathways as performing it, and is equally valuable for building the habit of kindness.</p>
        <p>Both paths nurture empathy and compassion—just at different comfort levels.</p>
      </div>
    </motion.div>
  );
}

/** Main Chapter 4 Cue Choice Component */
export function Chapter4CueChoice({ onComplete, isCompleted, trackButton }: Chapter4CueChoiceProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState<'spark' | 'quiet' | null>(null);
  const [showEducatorInfo, setShowEducatorInfo] = useState(false);

  const handleClose = () => {
    setSelectedMode(null);
    setShowEducatorInfo(false);
    setIsOpen(false);
  };

  const handleComplete = () => {
    onComplete();
    handleClose();
  };

  return (
    <>
      {/* Glowing trigger button */}
      <motion.button
        className="relative bg-pink-100 p-8 rounded-full border-4 border-white/70 shadow-2xl backdrop-blur-sm w-24 h-24 flex items-center justify-center"
        onClick={() => {
          trackButton?.('sparkling_petal_cue_open', { cue_name: 'Sparkling Petal' });
          setIsOpen(true);
        }}
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
        animate={{
          scale: [1, 1.25, 1],
          boxShadow: [
            "0 0 0 0 rgba(236, 72, 153, 0.7)",
            "0 0 0 30px rgba(236, 72, 153, 0)",
            "0 0 0 0 rgba(236, 72, 153, 0.7)",
          ],
        }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <Sparkles className="w-12 h-12 text-pink-600" />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="px-6 py-5 border-b border-gray-200 relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <div className="w-10 h-10 rounded-full bg-pink-50 text-pink-600 border-2 border-current flex items-center justify-center">
                    <Sparkles className="w-5 h-5" />
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={handleClose} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 h-8 w-8 p-0">
                  <X className="w-4 h-4" />
                </Button>
                <div className="text-center px-16">
                  <h3 className="text-xl font-bold">
                    <span className="font-serif font-bold bg-gradient-to-r from-pink-600 via-rose-500 to-pink-600 bg-clip-text text-transparent">Kindness Spark</span>
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <AnimatePresence mode="wait">
                  {showEducatorInfo && <EducatorInfo onClose={() => setShowEducatorInfo(false)} />}

                  {!selectedMode && !showEducatorInfo && (
                    <motion.div key="choice" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                      <p className="text-center text-gray-700 mb-2">Choose the path that feels right in this moment:</p>
                      <button onClick={() => setShowEducatorInfo(true)} className="flex items-center gap-1 mx-auto text-sm text-blue-600 hover:text-blue-800 mb-4">
                        <Info className="w-4 h-4" /> For Parents/Educators
                      </button>

                      {/* Option 1 */}
                      <motion.button
                        onClick={() => { trackButton?.('kindness_path_spark', { cue_name: 'Sparkling Petal' }); setSelectedMode('spark'); }}
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        className="w-full p-6 bg-gradient-to-br from-pink-50 to-rose-50 border-2 border-pink-300 rounded-xl text-left hover:border-pink-400 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-pink-500 flex items-center justify-center flex-shrink-0">
                            <Sparkles className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-pink-900 mb-1">Kindness Spark!</h4>
                            <p className="text-sm text-pink-700">Chant, tap your heart, and say one kind word out loud</p>
                          </div>
                        </div>
                      </motion.button>

                      {/* Option 2 */}
                      <motion.button
                        onClick={() => { trackButton?.('kindness_path_quiet', { cue_name: 'Sparkling Petal' }); setSelectedMode('quiet'); }}
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        className="w-full p-6 bg-gradient-to-br from-rose-50 to-orange-50 border-2 border-rose-300 rounded-xl text-left hover:border-rose-400 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-rose-400 flex items-center justify-center flex-shrink-0">
                            <Hand className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-rose-900 mb-1">Quiet Acts</h4>
                            <p className="text-sm text-rose-700">Think of one small kind thing you could quietly do — no performance needed</p>
                          </div>
                        </div>
                      </motion.button>

                      <p className="text-center text-sm text-gray-500 mt-4">Kindness looks different for everyone — both paths count!</p>
                    </motion.div>
                  )}

                  {selectedMode && !showEducatorInfo && (
                    <ActivityMode key={selectedMode} mode={selectedMode} onBack={() => setSelectedMode(null)} onComplete={handleComplete} />
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
